import { useId, type ReactNode } from "react";
import { Check } from "lucide-react";
import { COMPANY } from "../legal/companyDetails";
import type { PriceListCopy } from "../legal/priceListCopy";
import { cn } from "../ui/utils";

/**
 * The price sheet the welcome page shows. The contract is counsel's price
 * list at /subscription (`legal/vop/vopPriceList.ts`), a text a sheet with
 * examples and a euro guide may not be added to, so it is drawn there no
 * more. Presentational: every word and every figure comes in through
 * `copy` (`legal/priceListCopy.ts`, built from the price list the billing
 * engine is held to), and nothing here states a price of its own.
 *
 * One ruled sheet rather than two cards side by side: the two products, what
 * both include, what is agreed individually and the small print are bands of
 * one document, parted by hairlines, the way a printed price list is set. The
 * only thing in display type is each product's monthly price of a property;
 * what each room adds stands under it on a line of its own, in the body face,
 * and under the product's audience an example adds the two up.
 *
 * It lays itself out by its own width (`@container/price-list`), never the
 * window's: the legal page's column is 45rem at most and the welcome page's
 * 69rem, and neither is the viewport. Below 36rem of sheet the products stack
 * with the rule between them and the checklist is one column; from 36rem both
 * are two. A container query's rem is the root's (18px, 20px under high
 * contrast, 2px less below a 640px window), so the threshold moves with the
 * type it makes room for.
 *
 * Measured 5 Oct 2026 in headless Chromium, in all four skins, Czech and
 * English, on both pages, from a 320px window to a 1920px one, with and
 * without the 17px a classic scrollbar takes (800 renders; no page scrolled
 * sideways and nothing left the sheet):
 *
 * - 36rem falls between the widths common screens leave the sheet rather
 *   than on one. A 667px window leaves 34.1rem and stacks, a 720px one 37rem
 *   and a 768px tablet 39.7rem (38.7rem beside a classic scrollbar), both in
 *   two columns. Under high contrast a 768px window stacks (35.4rem) and an
 *   800px one does not (37rem). Windows of 624 to 639px are two columns as
 *   well, the root being 16px there: a column is 18rem, and the widest price
 *   has 64px to spare in it.
 * - A figure joined to its currency is one unbreakable run. The widest is
 *   "2,990 CZK" under high contrast, whose root is 18px on a phone: 189px at
 *   `text-5xl`, in the 219px a 320px phone leaves a column, or 202px beside a
 *   classic scrollbar. That is with 1.25rem of side padding; the 2rem the
 *   columns have from 36rem would leave it 14px short.
 */

interface PriceListPlansProps {
    /** The price list's words, in the page's language. */
    copy: PriceListCopy;
    /**
     * The level of the headings in the list: one under the heading of the
     * section it stands in. Both pages head that section with an h2.
     */
    headingLevel?: 2 | 3 | 4;
    className?: string;
    /** A call to action, drawn under the sheet. The list itself asks for nothing. */
    children?: ReactNode;
}

/** A band of the sheet below the products: ruled off from what is above it. */
const BAND = "border-t px-5 py-5 @min-[36rem]/price-list:px-8 @min-[36rem]/price-list:py-6";

/**
 * A band's title. A heading, so the list can be walked by headings, but set
 * as the kicker over each product is: the body face, not the heading's.
 */
const BAND_TITLE = "type-eyebrow font-sans";

export function PriceListPlans({ copy, headingLevel = 3, className, children }: PriceListPlansProps) {
    const id = useId();
    const Heading = `h${headingLevel}` as const;
    const includedId = `${id}-included`;

    // The one address in the list is where a price is agreed: a link, so a
    // reader on a phone writes with a tap.
    const [beforeAddress, afterAddress] = copy.agreement.split(COMPANY.email);
    const namesAddress = afterAddress !== undefined;

    return (
        <div data-slot="price-list" className={cn("@container/price-list", className)}>
            <div data-slot="price-list-sheet" className="overflow-hidden rounded-lg border bg-card text-card-foreground">
                {/* One explicit column until there is room for two: an
                    implicit one grows to its longest unbreakable line, and
                    takes the page past a phone's edge with it. */}
                <div className="grid grid-cols-1 @min-[36rem]/price-list:grid-cols-2">
                    {copy.products.map((product, index) => {
                        const nameId = `${id}-${product.key}`;
                        return (
                            <article
                                key={product.key}
                                aria-labelledby={nameId}
                                className={cn(
                                    "px-5 py-6 @min-[36rem]/price-list:p-8",
                                    index > 0 &&
                                        "border-t @min-[36rem]/price-list:border-t-0 @min-[36rem]/price-list:border-l",
                                )}
                            >
                                <p className="type-eyebrow">{product.kicker}</p>
                                <Heading id={nameId} className="type-display-section mt-2 text-lg">
                                    {product.name}
                                </Heading>

                                {/* The price and what it is charged by are one
                                    phrase, read out together. */}
                                <p className="mt-6 text-sm text-muted-foreground">
                                    <span className="block whitespace-nowrap text-card-foreground">
                                        <span className="type-display text-5xl tabular-nums">{product.price}</span>
                                        <>&nbsp;</>
                                        <span className="type-display-section text-xl text-muted-foreground">
                                            {product.currency}
                                        </span>
                                    </span>{" "}
                                    <span className="mt-2 block">{product.unit}</span>
                                </p>

                                {/* The second price: what every room adds
                                    to the one above. Its own line, or it
                                    reads as a caption of the first. The sign,
                                    the figure and the currency are joined in
                                    the copy; the words after them wrap. */}
                                <p className="mt-3 text-base font-medium leading-snug">{product.roomPrice}</p>

                                <p className="mt-3 font-mono text-xs leading-relaxed text-muted-foreground">
                                    {product.eurGuide}
                                </p>

                                <p className="mt-6 border-t border-border/70 pt-4 text-sm leading-relaxed">
                                    {product.audience}
                                </p>
                                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                                    {product.example}
                                </p>
                            </article>
                        );
                    })}
                </div>

                <div className={BAND}>
                    <Heading id={includedId} className={BAND_TITLE}>
                        {copy.includedTitle}
                    </Heading>
                    <ul
                        aria-labelledby={includedId}
                        className="mt-3 grid grid-cols-1 gap-x-10 @min-[36rem]/price-list:grid-cols-2"
                    >
                        {copy.included.map((line) => (
                            <li key={line} className="flex gap-3 border-t border-border/70 py-2.5 text-sm leading-snug">
                                {/* The tick is the list's meaning, drawn: the
                                    heading already says "included", so it is
                                    not read out with every line. */}
                                <Check aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-primary" />
                                <span className="min-w-0">{line}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className={BAND}>
                    <Heading className={BAND_TITLE}>{copy.agreementTitle}</Heading>
                    <p className="mt-2 text-sm leading-relaxed">
                        {namesAddress ? (
                            <>
                                {beforeAddress}
                                <a
                                    href={`mailto:${COMPANY.email}`}
                                    className="underline underline-offset-2 transition-colors hover:text-primary"
                                >
                                    {COMPANY.email}
                                </a>
                                {afterAddress}
                            </>
                        ) : (
                            copy.agreement
                        )}
                    </p>
                </div>

                <ul className={cn(BAND, "space-y-1 text-xs leading-relaxed text-muted-foreground")}>
                    {copy.smallPrint.map((line) => (
                        <li key={line}>{line}</li>
                    ))}
                </ul>
            </div>

            {children && <div className="mt-6">{children}</div>}
        </div>
    );
}
